import { colorName } from '../colorName';

/**
 * "Which one am I?" — one quiet line above every board (owner ask 2026-09-25: after a month
 * away you shouldn't have to work out your colour from the pieces). Left: your swatch and its
 * name, plus a kind-specific detail where the kind has one (Pool's solids/stripes, Beer Pong's
 * near end). Right: whose move it is, with the mover's swatch.
 *
 * Players only — a spectator has no side, and the header already says "Spectating". The turn
 * text is the board's own status line, so it stays a fact ("Faye's move", "You won"), never an
 * instruction (`memory/no-demand-mechanics.md`). No animation, on purpose.
 */
export function SideLine({
  color,
  detail,
  turnText,
  turnColor,
  myTurn,
}: {
  /** The viewer's member colour — the colour their pieces/shots are drawn in. */
  color: string;
  detail?: string;
  turnText: string;
  /** Swatch for whoever holds the move; `null` once the game is over (or between turns). */
  turnColor: string | null;
  myTurn: boolean;
}): JSX.Element {
  return (
    <div
      className="flex min-h-[40px] items-center justify-between gap-3 rounded-md border border-line bg-surface px-3 py-2"
      data-testid="game-side-line"
      data-my-turn={myTurn}
    >
      <span className="flex min-w-0 items-center gap-2 text-sm text-fg">
        <span
          className="h-4 w-4 shrink-0 rounded-full ring-1 ring-line-strong"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
        <span className="truncate" data-testid="game-side-label">
          You&rsquo;re <span className="font-semibold">{colorName(color)}</span>
          {detail ? <span className="text-fg-2"> · {detail}</span> : null}
        </span>
      </span>
      {turnText && (
        <span
          className={`flex shrink-0 items-center gap-1.5 text-sm ${
            myTurn ? 'font-semibold text-fg' : 'text-fg-2'
          }`}
          data-testid="game-side-turn"
        >
          {turnColor && (
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: turnColor }}
              aria-hidden="true"
            />
          )}
          {turnText}
        </span>
      )}
    </div>
  );
}
